import otel, { propagation, trace } from '@opentelemetry/api'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { W3CTraceContextPropagator } from '@opentelemetry/core'
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { type DetectedResourceAttributes, resourceFromAttributes } from '@opentelemetry/resources'
import { ConsoleMetricExporter, MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics'
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
  SimpleSpanProcessor,
  type SpanExporter,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web'
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions'

import { WebVitalsInstrumentation } from '../components/profiler'
import { APP_NAME, APP_OTEL_APM, APP_VERSION } from '../config/costants'
import { getDefaultOTELInstrumentations, initialIntrumentationsOptions } from './getDefaultOTELInstrumentations'
import { getUserAgentData } from './getUserAgentData'
import {
  MetricsExporter,
  type MetricsInstrumentationOptions,
  TracesExporter,
  type TracingInstrumentationOptions,
} from './types'
import { windowsErrorTrap } from './windowErrorsTrap'

/**
 *  The `MetricsInstrumentation` class sets up OpenTelemetry metrics for a web application, including a
 * `WebTracerProvider`, span exporter, and error trapping.
 */
export class MetricsInstrumentation {
  private webVitals: WebVitalsInstrumentation | undefined

  constructor(private opts: MetricsInstrumentationOptions = {}, private readonly apmEndpointUrl?: string) {
    this.opts.exporterType = opts.exporterType ?? MetricsExporter.CONSOLE
    this.opts.headers = opts.headers ?? {}
  }

  /**
   * The `initialize` method is setting up OpenTelemetry  metrics for a web application.
   * It creates a `WebTracerProvider` with a specified resource and sets up a span
   * exporter based on the specified exporter type. It also registers instrumentations and sets
   * up error trapping for non-React exceptions.
   */
  initialize() {
    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: this.opts.app?.name ?? APP_NAME,
      [ATTR_SERVICE_VERSION]: this.opts.app?.version ?? APP_VERSION,
    })
    const metricReader = new PeriodicExportingMetricReader({
      exporter:
        this.opts.exporterType === MetricsExporter.OTLP
          ? new OTLPMetricExporter({
              url: `${this.apmEndpointUrl ?? APP_OTEL_APM}/v1/metrics`, // http://localhost:4318/v1/metrics
              headers: this.opts.headers,
            })
          : new ConsoleMetricExporter(),
      exportIntervalMillis: 2 * 60 * 1000,
    })

    const myServiceMeterProvider = new MeterProvider({
      resource,
      readers: [metricReader],
    })

    // Set this MeterProvider to be global to the app being instrumented.
    otel.metrics.setGlobalMeterProvider(myServiceMeterProvider)
    this.webVitals = new WebVitalsInstrumentation()
    this.webVitals.map()
  }
}

/**
 * The TracingInstrumentation class provides a way to initialize OpenTelemetry tracing for a web application.
 */
export class TracingInstrumentation {
  constructor(private options: TracingInstrumentationOptions = {}, private readonly apmEndpointUrl?: string) {
    this.options.headers = options.headers ?? {}
    this.options.concurrencyLimit = options.concurrencyLimit ?? 10
    this.options.onExceptionAction = options.onExceptionAction
    this.options.exporterType = options.exporterType ?? TracesExporter.CONSOLE
    // instrumentation options parsing
    if (options.instrumentationOptions?.propagateTraceHeaderCorsUrls) {
      options.instrumentationOptions.propagateTraceHeaderCorsUrls =
        options.instrumentationOptions?.propagateTraceHeaderCorsUrls.map((e) => RegExp(e))
    }
    this.options.instrumentationOptions = options.instrumentationOptions ?? initialIntrumentationsOptions
  }

  /**
   *  The `initialize` method is setting up OpenTelemetry tracing for a web application.
   */
  initialize(): void {
    const options = this.options

    const attributes: DetectedResourceAttributes = {}

    attributes[ATTR_SERVICE_NAME] = options.app?.name ?? APP_NAME
    attributes[ATTR_SERVICE_VERSION] = options.app?.version ?? APP_VERSION

    Object.assign(attributes, options.resourceAttributes)
    Object.assign(attributes, getUserAgentData())

    const resource = resourceFromAttributes(attributes)

    let provider: WebTracerProvider

    const collectorOptions = {
      url: `${this.apmEndpointUrl ?? APP_OTEL_APM}/v1/traces`, // url is optional and can be omitted - default is http://localhost:4318/v1/traces
      headers: this.options.headers, // an optional object containing custom headers to be sent with each request
      concurrencyLimit: this.options.concurrencyLimit, // an optional limit on pending requests
    }
    let exporter: SpanExporter | null | SimpleSpanProcessor = null
    // set exporter
    if (this.options.exporterType === TracesExporter.CONSOLE) {
      exporter = new SimpleSpanProcessor(new ConsoleSpanExporter())
      provider = new WebTracerProvider({
        resource,
        spanProcessors: [exporter],
      })
    }
    if (this.options.exporterType === TracesExporter.OTLP) {
      exporter = new OTLPTraceExporter(collectorOptions)
      provider = new WebTracerProvider({
        resource,
        spanProcessors: [
          new BatchSpanProcessor(exporter, {
            // The maximum queue size. After the size is reached spans are dropped.
            maxQueueSize: 100,
            // The maximum batch size of every export. It must be smaller or equal to maxQueueSize.
            maxExportBatchSize: 10,
            // The interval between two consecutive exports
            scheduledDelayMillis: 500,
            // How long the export can run before it is cancelled
            exportTimeoutMillis: 30000,
          }),
        ],
      })

      provider.register({
        propagator: new W3CTraceContextPropagator(),
        contextManager: new ZoneContextManager(),
      })

      registerInstrumentations({
        instrumentations: [
          // options.instrumentations ??
          ...getDefaultOTELInstrumentations({
            // ignoreUrls: this.getIgnoreUrls(),
            propagateTraceHeaderCorsUrls: this.options.instrumentationOptions?.propagateTraceHeaderCorsUrls,
          }),
        ],
      })

      trace.setGlobalTracerProvider(provider)

      // Trap windows errors for non React exceptions
      window.onerror = (
        message: string | Event,
        source: string | undefined,
        lineno: number | undefined,
        colno: number | undefined,
        error: Error | undefined,
      ) => {
        windowsErrorTrap(error, message, source, lineno, colno)
        this.options.onExceptionAction?.(error)
        return true
      }
      // Trap errors that is sent to the global scope of a script when a JavaScript Promise that has no rejection handler is rejected
      window.onunhandledrejection = (e) => {
        windowsErrorTrap(e.reason)
        this.options.onExceptionAction?.(e.reason)
        e.preventDefault()
        return true
      }

      propagation.setGlobalPropagator(new W3CTraceContextPropagator()) // check if this line was needed
    }
  }
}
